import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { AddPeopleFileStep } from './AddPeopleFileStep';

/** Which step the overlay opens on: choosing a file, or watching the work that's already going. */
export type AddPeopleStep = 'file' | 'watch';

/**
 * Adding people and matching them, as one overlay: Your file (the one step that asks for input), then
 * the stages to watch. It opens only on a person's action and can be closed at any point; closing it
 * stops nothing.
 */
export function AddPeopleOverlay({
  open,
  onOpenChange,
  step,
  onUploadStarted,
  onClosed,
  initialFile = null,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: AddPeopleStep;
  onUploadStarted: (prospectListId: string) => void;
  /** Where focus goes once it has closed. */
  onClosed: () => void;
  /** A file dropped before the overlay opened, for the file step to check straight away. */
  initialFile?: File | null;
  /** The stages, shown on the watch step. */
  children: React.ReactNode;
}) {
  const [wide, setWide] = useState(false);
  const fileStep = step === 'file';
  // However it was closed, the next file step starts at the calm width.
  useEffect(() => {
    if (!open) setWide(false);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          onClosed();
        }}
        className={cn(
          'max-h-[90vh] gap-0 overflow-y-auto p-0',
          fileStep ? (wide ? 'max-w-[900px]' : 'max-w-[640px]') : 'max-w-[640px] border-0 bg-transparent shadow-none',
        )}
      >
        <DialogTitle className="sr-only">Add people</DialogTitle>
        <DialogDescription className="sr-only">
          Add people from a file and match them to federal giving, stage by stage.
        </DialogDescription>
        {fileStep ? <AddPeopleFileStep onStarted={onUploadStarted} onWideChange={setWide} initialFile={initialFile} /> : children}
      </DialogContent>
    </Dialog>
  );
}
